import { roundCurrency, toDecimal } from "@/lib/utils/math";

export interface ROIInput {
  initialInvestment: number;
  finalValue: number;
  years: number;
  /** Additional costs/expenses to subtract from gain (default 0) */
  additionalCosts?: number;
  /** Inflation rate % for real ROI (default 0) */
  inflationRate?: number;
  /** Tax rate % on gains for after-tax ROI (default 0) */
  taxRate?: number;
  /** Scenario rates for comparison (default 3, 6, 9) */
  lowRate?: number;
  mediumRate?: number;
  highRate?: number;
}

export interface ROIScenario {
  label: string;
  ratePercent: number;
  finalValue: number;
  roiPercent: number;
  netProfit: number;
}

export interface ROIResult {
  roiPercent: number;
  netProfit: number;
  annualizedROI: number;
  paybackYears: number | null;
  chartData: { year: number; value: number }[];
  /** Scenario comparison: low/med/high return cases */
  scenarios?: ROIScenario[];
  /** Inflation-adjusted (real) ROI when inflationRate > 0 */
  realROI?: number;
  /** After-tax net profit when taxRate > 0 */
  afterTaxNetProfit?: number;
  /** After-tax ROI when taxRate > 0 */
  afterTaxROI?: number;
}

export function calculateROI(input: ROIInput): ROIResult {
  const {
    initialInvestment,
    finalValue,
    years,
    additionalCosts = 0,
    inflationRate = 0,
    taxRate = 0,
  } = input;

  const grossProfit = finalValue - initialInvestment;
  const netProfit = grossProfit - additionalCosts;
  const roiPercent =
    initialInvestment > 0
      ? (netProfit / initialInvestment) * 100
      : 0;

  const annualizedROI =
    years > 0 && initialInvestment > 0 && finalValue > 0
      ? (Math.pow(finalValue / initialInvestment, 1 / years) - 1) * 100
      : 0;

  const annualGain = years > 0 ? netProfit / years : 0;
  const paybackYears =
    annualGain > 0 && initialInvestment > 0 && netProfit > 0
      ? initialInvestment / annualGain
      : null;

  const chartData: { year: number; value: number }[] = [];
  const cagr = annualizedROI / 100;
  for (let y = 0; y <= years; y++) {
    const value =
      y === 0
        ? initialInvestment
        : roundCurrency(
            toDecimal(initialInvestment * Math.pow(1 + cagr, y))
          );
    chartData.push({ year: y, value });
  }

  const scenarios: ROIScenario[] | undefined =
    initialInvestment > 0 && years > 0
      ? [
          {
            label: "Low",
            ratePercent: input.lowRate ?? 3,
            finalValue: 0,
            roiPercent: 0,
            netProfit: 0,
          },
          {
            label: "Medium",
            ratePercent: input.mediumRate ?? 6,
            finalValue: 0,
            roiPercent: 0,
            netProfit: 0,
          },
          {
            label: "High",
            ratePercent: input.highRate ?? 9,
            finalValue: 0,
            roiPercent: 0,
            netProfit: 0,
          },
        ].map((s) => {
          const fv = initialInvestment * Math.pow(1 + s.ratePercent / 100, years);
          const profit = fv - initialInvestment - additionalCosts;
          const roi = (profit / initialInvestment) * 100;
          return {
            ...s,
            finalValue: roundCurrency(toDecimal(fv)),
            netProfit: roundCurrency(toDecimal(profit)),
            roiPercent: roundCurrency(toDecimal(roi)),
          };
        })
      : undefined;

  const realROI =
    inflationRate > 0
      ? ((1 + roiPercent / 100) / (1 + inflationRate / 100) - 1) * 100
      : undefined;

  const afterTaxNetProfit =
    taxRate > 0 ? netProfit * (1 - taxRate / 100) : undefined;
  const afterTaxROI =
    taxRate > 0 && initialInvestment > 0 && afterTaxNetProfit != null
      ? (afterTaxNetProfit / initialInvestment) * 100
      : undefined;

  return {
    roiPercent: roundCurrency(toDecimal(roiPercent)),
    netProfit: roundCurrency(toDecimal(netProfit)),
    annualizedROI: roundCurrency(toDecimal(annualizedROI)),
    paybackYears:
      paybackYears != null ? roundCurrency(toDecimal(paybackYears)) : null,
    chartData,
    scenarios,
    realROI: realROI != null ? roundCurrency(toDecimal(realROI)) : undefined,
    afterTaxNetProfit:
      afterTaxNetProfit != null
        ? roundCurrency(toDecimal(afterTaxNetProfit))
        : undefined,
    afterTaxROI:
      afterTaxROI != null ? roundCurrency(toDecimal(afterTaxROI)) : undefined,
  };
}
